import { ChevronDown, LoaderCircle, Minus, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addCategoryItem,
  categoryItemFromLabels,
  categoryItemFromValue,
  ensureCategoryItems,
  mergeCategoryItems,
  removeEmptyCategory,
  sameCategoryValue,
  subscribeToCategorySet,
} from '../services/categories';

const EMPTY_LABELS = { gu: '', hi: '', en: '' };
const LANGUAGES = [
  ['gu', 'ગુજરાતી'],
  ['hi', 'हिन्दी'],
  ['en', 'English'],
];

function CategoryLabel({ item }) {
  const primary = item?.label?.en || item?.label?.gu || item?.label?.hi || 'Untitled category';
  const secondary = [item?.label?.gu, item?.label?.hi]
    .filter((label) => label && label !== primary)
    .join(' · ');
  return <span className="category-label"><strong>{primary}</strong>{secondary ? <small>{secondary}</small> : null}</span>;
}

export default function CategoryField({
  field,
  value,
  onChange,
  moduleKey,
  records = [],
  canDelete = false,
  interactionLocked = false,
}) {
  const rootRef = useRef(null);
  const [storedItems, setStoredItems] = useState([]);
  const [categorySetExists, setCategorySetExists] = useState(null);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [labels, setLabels] = useState(EMPTY_LABELS);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const seedItems = useMemo(() => {
    const configured = (categorySetExists === false ? field.initialCategories || [] : [])
      .map((item) => categoryItemFromValue(item.value, field.storage, item.label));
    const fromRecords = records
      .map((record) => categoryItemFromValue(record?.[field.key], field.storage));
    const current = categoryItemFromValue(value, field.storage);
    return mergeCategoryItems(configured, fromRecords, [current]);
  }, [categorySetExists, field.initialCategories, field.key, field.storage, records, value]);

  const seedSignature = useMemo(
    () => JSON.stringify(seedItems.map((item) => item.value)),
    [seedItems],
  );

  useEffect(() => subscribeToCategorySet(moduleKey, (items, metadata) => {
    setStoredItems(items);
    setCategorySetExists(metadata.exists);
    setLoading(false);
  }, (loadError) => {
    setError(loadError.message || 'Categories load thai shaki nahi.');
    setLoading(false);
  }), [moduleKey]);

  useEffect(() => {
    if (categorySetExists === null || !seedItems.length) return;
    ensureCategoryItems(moduleKey, seedItems).catch((seedError) => {
      setError(seedError.message || 'Current categories save thai shaki nahi.');
    });
  }, [categorySetExists, moduleKey, seedItems, seedSignature]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        setAdding(false);
      }
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [open]);

  const items = useMemo(
    () => mergeCategoryItems(storedItems, seedItems),
    [seedItems, storedItems],
  );
  const selected = items.find((item) => sameCategoryValue(item.value, value));
  const usageCount = (item) => records
    .filter((record) => sameCategoryValue(record?.[field.key], item.value)).length;

  const selectItem = (item) => {
    onChange(structuredClone(item.value));
    setOpen(false);
    setAdding(false);
    setError('');
  };

  const addItem = async () => {
    setBusy(true);
    setError('');
    try {
      const item = categoryItemFromLabels(labels, field.storage);
      const saved = await addCategoryItem(moduleKey, item);
      onChange(structuredClone(saved.value));
      setLabels(EMPTY_LABELS);
      setAdding(false);
      setOpen(false);
    } catch (addError) {
      setError(addError.message || 'Category add thai shaki nahi.');
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (item) => {
    setBusy(true);
    setError('');
    try {
      await removeEmptyCategory(moduleKey, item, moduleKey, field.key);
      if (sameCategoryValue(value, item.value)) onChange(field.storage === 'translated' ? { ...EMPTY_LABELS } : '');
    } catch (removeError) {
      setError(removeError.message || 'Category remove thai shaki nahi.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="field category-field" ref={rootRef}>
      <span>{field.label}{field.required ? <em>*</em> : null}</span>
      <button
        className={`category-select-trigger ${open ? 'open' : ''}`}
        type="button"
        disabled={interactionLocked}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {selected ? <CategoryLabel item={selected} /> : <span className="category-placeholder">Select category…</span>}
        <ChevronDown size={18} />
      </button>

      {open ? (
        <div className="category-popover">
          <div className="category-options">
            {loading ? <div className="category-loading"><LoaderCircle className="spin" size={17} /> Loading categories…</div> : null}
            {!loading && !items.length ? <div className="category-empty">No category added yet.</div> : null}
            {items.map((item) => {
              const usedBy = usageCount(item);
              const active = sameCategoryValue(item.value, value);
              return (
                <div className={`category-option ${active ? 'selected' : ''}`} key={item.id}>
                  <button type="button" className="category-option-main" onClick={() => selectItem(item)}>
                    <CategoryLabel item={item} />
                    <small>{usedBy} record{usedBy === 1 ? '' : 's'}</small>
                  </button>
                  {canDelete && usedBy === 0 ? (
                    <button
                      type="button"
                      className="category-remove"
                      title="Remove empty category"
                      aria-label={`Remove ${item.label?.en || 'category'}`}
                      disabled={busy}
                      onClick={() => removeItem(item)}
                    ><Minus size={16} /></button>
                  ) : null}
                </div>
              );
            })}
          </div>

          {adding ? (
            <div className="category-add-form">
              <strong>Add new category</strong>
              <div className="category-translation-grid">
                {LANGUAGES.map(([language, label]) => (
                  <label key={language}>
                    <span>{label}</span>
                    <input
                      value={labels[language]}
                      disabled={busy}
                      onChange={(event) => setLabels((current) => ({ ...current, [language]: event.target.value }))}
                    />
                  </label>
                ))}
              </div>
              <div className="category-add-actions">
                <button type="button" className="button secondary compact" disabled={busy} onClick={() => setAdding(false)}>Cancel</button>
                <button type="button" className="button primary compact" disabled={busy} onClick={addItem}>
                  {busy ? <LoaderCircle className="spin" size={15} /> : <Plus size={15} />} Add category
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="category-add-button" onClick={() => setAdding(true)}>
              <Plus size={16} /> Add new category
            </button>
          )}
        </div>
      ) : null}
      {error ? <small className="field-error">{error}</small> : null}
      <small>Categories permanently save thase. Red − button fakt empty category par j dekhase.</small>
    </div>
  );
}
